/**
 * User CRUD Modals Component
 *
 * Provides UI for creating, editing, and deleting users
 * Note: User creation via frontend has limitations - ideally should use backend callable
 */

import { auth, db } from '@/core/firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { currentUser, setAdminCredentials } from '@/core/state'

/**
 * User data structure
 */
interface UserData {
  uid: string
  email: string | null
  role: 'user' | 'admin'
  createdAt: Date | { seconds: number, nanoseconds: number } | string
  [key: string]: unknown
}

/**
 * Show create user modal
 *
 * WARNING: Firebase Auth limitation - creating a user logs you out as the current user
 * We work around this by re-authenticating the admin after user creation
 */
export function showCreateUserModal(): void {
  const modal = document.createElement('div')
  modal.className
    = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-bold text-gray-900">Create New User</h2>
          <button
            id="close-create-user-modal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Warning -->
        <div class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-xs text-yellow-800">
            <strong>Note:</strong> Creating a user will temporarily log you out. You'll be automatically logged back in.
          </p>
        </div>

        <!-- Form -->
        <form id="create-user-form" class="space-y-4">
          <!-- Email -->
          <div>
            <label for="new-user-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="new-user-email"
              placeholder="user@example.com"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <!-- Password -->
          <div>
            <label for="new-user-password" class="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <input
              type="password"
              id="new-user-password"
              placeholder="Minimum 6 characters"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              minlength="6"
            />
            <p class="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
          </div>

          <!-- Role -->
          <div>
            <label for="new-user-role" class="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              id="new-user-role"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <!-- Error Message -->
          <div id="create-user-error" class="hidden p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600"></p>
          </div>

          <!-- Success Message -->
          <div id="create-user-success" class="hidden p-3 bg-green-50 border border-green-200 rounded-lg">
            <p class="text-sm text-green-600">User created successfully!</p>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-2">
            <button
              type="submit"
              id="create-user-submit-btn"
              class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create User
            </button>
            <button
              type="button"
              id="cancel-create-user-btn"
              class="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  // Get DOM elements
  const form = modal.querySelector('#create-user-form') as HTMLFormElement
  const closeBtn = modal.querySelector('#close-create-user-modal')
  const cancelBtn = modal.querySelector('#cancel-create-user-btn')
  const submitBtn = modal.querySelector(
    '#create-user-submit-btn',
  ) as HTMLButtonElement
  const emailInput = modal.querySelector(
    '#new-user-email',
  ) as HTMLInputElement
  const passwordInput = modal.querySelector(
    '#new-user-password',
  ) as HTMLInputElement
  const roleSelect = modal.querySelector(
    '#new-user-role',
  ) as HTMLSelectElement
  const errorDiv = modal.querySelector('#create-user-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  const successDiv = modal.querySelector('#create-user-success') as HTMLElement

  // Close modal function
  const closeModal = () => {
    modal.remove()
  }

  // Event listeners
  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal()
    }
  })

  // Form submit
  form?.addEventListener('submit', async (e) => {
    e.preventDefault()

    const email = emailInput.value.trim()
    const password = passwordInput.value
    const role = roleSelect.value as 'user' | 'admin'

    // Validation
    if (!email || !password) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Please fill in all required fields'
      return
    }

    if (password.length < 6) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Password must be at least 6 characters'
      return
    }

    // Get admin credentials for re-authentication
    if (!auth.currentUser) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'You must be logged in as admin'
      return
    }

    const adminEmail = auth.currentUser.email
    const adminPassword = prompt(
      'To create a new user, please re-enter your admin password for confirmation:',
    )

    if (!adminPassword) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Admin password not provided. User creation cancelled.'
      return
    }

    // Store admin credentials for re-authentication
    setAdminCredentials({ email: adminEmail!, password: adminPassword })

    // Hide errors and disable button
    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')
    submitBtn.disabled = true
    submitBtn.textContent = 'Creating...'

    try {
      console.log('[User CRUD] Creating new user:', email)

      // Create user (this will log us out)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      )
      const newUser = userCredential.user

      // Create Firestore document for the new user
      await setDoc(doc(db, 'users', newUser.uid), {
        uid: newUser.uid,
        email: newUser.email,
        role,
        createdAt: new Date(),
      })

      // If creating an admin user, also create adminUsers document
      if (role === 'admin') {
        await setDoc(doc(db, 'adminUsers', newUser.uid), {
          userId: newUser.uid,
          email: newUser.email,
          role: 'admin',
          assignedAt: new Date(),
          assignedBy: auth.currentUser?.uid || 'UNKNOWN',
        })
        console.log('[User CRUD] Admin user document created in adminUsers collection')
      }

      console.log('[User CRUD] User created successfully:', newUser.uid)

      // Show success message
      successDiv.classList.remove('hidden')
      submitBtn.textContent = 'User Created!'

      // Re-authenticate as admin
      console.log('[User CRUD] Re-authenticating as admin...')
      await signInWithEmailAndPassword(auth, adminEmail!, adminPassword)

      // Close modal and refresh after short delay
      setTimeout(() => {
        closeModal()
        window.location.reload()
      }, 1500)
    } catch (error: unknown) {
      console.error('[User CRUD] Create user failed:', error)
      errorDiv.classList.remove('hidden')
      errorText.textContent
        = (error as Error)?.message || 'Failed to create user. Please try again.'
      submitBtn.disabled = false
      submitBtn.textContent = 'Create User'

      // Clear admin credentials on error
      setAdminCredentials(null)
    }
  })
}

/**
 * Show edit user modal
 */
export function showEditUserModal(user: UserData): void {
  const modal = document.createElement('div')
  modal.className
    = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit User</h2>
          <button
            id="close-edit-user-modal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- User Info -->
        <div class="mb-4 bg-gray-50 rounded-lg p-3">
          <div class="text-sm text-gray-600">User ID</div>
          <div class="text-sm font-mono text-gray-900">${user.uid}</div>
        </div>

        <!-- Form -->
        <form id="edit-user-form" class="space-y-4">
          <!-- Email -->
          <div>
            <label for="edit-user-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="edit-user-email"
              value="${user.email || ''}"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <p class="mt-1 text-xs text-gray-500">Note: This only updates Firestore, not Firebase Auth</p>
          </div>

          <!-- Role -->
          <div>
            <label for="edit-user-role" class="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              id="edit-user-role"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="user" ${user.role === 'user' ? 'selected' : ''}>User</option>
              <option value="admin" ${user.role === 'admin' ? 'selected' : ''}>Admin</option>
            </select>
            <p class="mt-1 text-xs text-gray-500">Note: Role is stored in Firestore only</p>
          </div>

          <!-- Error Message -->
          <div id="edit-user-error" class="hidden p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600"></p>
          </div>

          <!-- Success Message -->
          <div id="edit-user-success" class="hidden p-3 bg-green-50 border border-green-200 rounded-lg">
            <p class="text-sm text-green-600">User updated successfully!</p>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-2">
            <button
              type="submit"
              id="edit-user-submit-btn"
              class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Changes
            </button>
            <button
              type="button"
              id="cancel-edit-user-btn"
              class="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  // Get DOM elements
  const form = modal.querySelector('#edit-user-form') as HTMLFormElement
  const closeBtn = modal.querySelector('#close-edit-user-modal')
  const cancelBtn = modal.querySelector('#cancel-edit-user-btn')
  const submitBtn = modal.querySelector(
    '#edit-user-submit-btn',
  ) as HTMLButtonElement
  const emailInput = modal.querySelector(
    '#edit-user-email',
  ) as HTMLInputElement
  const roleSelect = modal.querySelector(
    '#edit-user-role',
  ) as HTMLSelectElement
  const errorDiv = modal.querySelector('#edit-user-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  const successDiv = modal.querySelector('#edit-user-success') as HTMLElement

  // Close modal function
  const closeModal = () => {
    modal.remove()
  }

  // Event listeners
  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal()
    }
  })

  // Form submit
  form?.addEventListener('submit', async (e) => {
    e.preventDefault()

    const newEmail = emailInput.value.trim()
    const newRole = roleSelect.value as 'user' | 'admin'

    // Validation
    if (!newEmail) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Email is required'
      return
    }

    // Check if anything changed
    if (newEmail === user.email && newRole === user.role) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'No changes detected'
      return
    }

    // Hide errors and disable button
    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')
    submitBtn.disabled = true
    submitBtn.textContent = 'Saving...'

    try {
      console.log('[User CRUD] Updating user:', user.uid)

      // Update Firestore document
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, {
        email: newEmail,
        role: newRole,
      })

      // Handle adminUsers collection based on role change
      const oldRole = user.role
      const adminUserRef = doc(db, 'adminUsers', user.uid)

      if (newRole === 'admin' && oldRole !== 'admin') {
        // User became admin - create adminUsers document
        await setDoc(adminUserRef, {
          userId: user.uid,
          email: newEmail,
          role: 'admin',
          assignedAt: new Date(),
          assignedBy: auth.currentUser?.uid || 'UNKNOWN',
        })
        console.log('[User CRUD] Admin user document created in adminUsers collection')
      } else if (newRole !== 'admin' && oldRole === 'admin') {
        // User is no longer admin - remove adminUsers document
        await deleteDoc(adminUserRef)
        console.log('[User CRUD] Admin user document removed from adminUsers collection')
      } else if (newRole === 'admin' && oldRole === 'admin' && newEmail !== user.email) {
        // Admin email changed - update adminUsers document
        await updateDoc(adminUserRef, {
          email: newEmail,
        })
        console.log('[User CRUD] Admin user email updated in adminUsers collection')
      }

      console.log('[User CRUD] User updated successfully')

      // Show success message
      successDiv.classList.remove('hidden')
      submitBtn.textContent = 'Saved!'

      // Close modal and refresh after short delay
      setTimeout(() => {
        closeModal()
        window.location.reload()
      }, 1500)
    } catch (error: unknown) {
      console.error('[User CRUD] Update user failed:', error)
      errorDiv.classList.remove('hidden')
      errorText.textContent
        = (error as Error)?.message || 'Failed to update user. Please try again.'
      submitBtn.disabled = false
      submitBtn.textContent = 'Save Changes'
    }
  })
}

/**
 * Delete user with confirmation
 *
 * WARNING: This only deletes the Firestore document, not the Firebase Auth account
 */
export async function deleteUser(userId: string, userEmail: string | null): Promise<void> {
  // Safety check - don't delete yourself
  if (userId === currentUser?.uid) {
    alert('For safety, you cannot delete your own user record.')
    return
  }

  const confirmed = confirm(
    `Are you sure you want to delete the user "${userEmail || userId}"?\n\nWARNING: This will delete the Firestore document but NOT the Firebase Auth account. The user will still be able to log in.`,
  )

  if (!confirmed) {
    return
  }

  try {
    console.log('[User CRUD] Deleting user:', userId)

    await deleteDoc(doc(db, 'users', userId))

    console.log('[User CRUD] User deleted successfully')
    alert('User Firestore record deleted successfully.')

    // Refresh page to update user list
    window.location.reload()
  } catch (error: unknown) {
    console.error('[User CRUD] Delete user failed:', error)
    alert(`Error deleting user: ${(error as Error).message}`)
  }
}

/**
 * Load all users from Firestore
 */
export async function loadAllUsersFromFirestore(): Promise<UserData[]> {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'))
    const users: UserData[] = []

    querySnapshot.forEach((docSnap) => {
      users.push({
        uid: docSnap.id,
        ...docSnap.data(),
      } as UserData)
    })

    return users
  } catch (error) {
    console.error('[User CRUD] Error loading users:', error)
    throw error
  }
}

/**
 * Initialize user CRUD component
 * (Called from main admin panel)
 */
export function initializeUserCRUD(): void {
  console.log('[User CRUD] Initialized')
  // CRUD modals are shown on demand via exported functions
}
